import { memo as Memo, useEffect, useCallback, useRef, useState } from 'react'
import gsap from 'gsap'
import Draggable from 'gsap/dist/Draggable'

//* HOCs
import { withUIContext } from 'context'

//* Components
import Image from 'components/common/Image'

//* Styles 
import CardsEffectSliderStyle from './style'

const CardsEffectSlider = Memo(({ images, winWidth }) => {

    //! Refs
    const itemRefs = useRef([])
    const trTime = useRef(0.5)
    const isRight = useRef()
    const diff = useRef(50)
    const rotate = useRef(10)
    const scale = useRef(0.818)

    //! States
    const [active, setActive] = useState(0)

    //! Animate Left or Right Slide
    const animateLeftRightSlide = useCallback(({ element, symbol, isKeyframe, zIndex }) => {
        return gsap.to(element, {
            duration: trTime.current,
            scale: scale.current,
            x: `${symbol}${diff.current}%`,
            rotate: `${symbol}${rotate.current}`,
            zIndex: zIndex,
            keyframes: isKeyframe && [
                {
                    x: `${symbol}110%`,
                    duration: trTime.current / 2,
                    rotate: 0,
                },
                {
                    zIndex: zIndex,
                },
                {
                    x: `${symbol}${diff.current}%`,
                    duration: trTime.current / 2,
                    rotate: `${symbol}${rotate.current}`,
                    scale: scale.current,
                }
            ]
        })
    }, [trTime.current, scale.current, diff.current, rotate.current, isRight.current])

    //! Change Slide
    useEffect(() => {
        const lastItemIndex = itemRefs.current.length - 1
        const prevIndex = active === 0 ? lastItemIndex : active - 1
        const nextIndex = active === lastItemIndex ? 0 : active + 1

        if (itemRefs.current[active]) {
            gsap.to(itemRefs.current[active], {
                zIndex: 3,
                duration: trTime.current / 2,
            })
            gsap.to(itemRefs.current[active], {
                delay: 0.1,
                duration: trTime.current - 0.1,
                x: 0,
                scale: 1,
                rotate: 0
            })
        }

        const zIndexNext = isRight.current ? 1 : 2
        const zIndexPrev = zIndexNext === 1 ? 2 : 1

        animateLeftRightSlide({
            element: itemRefs.current[prevIndex],
            symbol: '-',
            isKeyframe: typeof isRight.current === 'boolean' && !isRight.current,
            zIndex: zIndexPrev
        })

        animateLeftRightSlide({
            element: itemRefs.current[nextIndex],
            symbol: '+',
            isKeyframe: isRight.current,
            zIndex: zIndexNext
        })
    }, [active, itemRefs.current])

    //! Update Slider 
    const animate = useCallback((isRight) => {
        const lastItemIndex = itemRefs.current.length - 1
        setActive(isRight ? (active === 0 ? lastItemIndex : active - 1) : (active === lastItemIndex ? 0 : active + 1))
    }, [active, itemRefs.current, isRight.current])

    //! Slider Init
    useEffect(() => {
        const elSizes = itemRefs.current[active].getBoundingClientRect().width / 2

        itemRefs.current.length && Draggable.create(itemRefs.current, {
            type: "x",
            edgeResistance: 1,
            bounds: { maxX: elSizes, minX: -elSizes },
            minimumMovement: winWidth > 1280 ? 6 : 20,
            inertia: true,
            snap: {
                x: function (endValue) {
                    isRight.current = this.startX - endValue < 0; animate(isRight.current); return this.startX
                },
            },
            zIndexBoost: false
        });
    }, [active])

    return (
        <CardsEffectSliderStyle className="ces-module">
            <div className="ces-container">
                <div className="ces-inner-container">
                    {images.slice(0, 3).map((i, k) => (
                        <div ref={ref => !itemRefs.current[k] && itemRefs.current.push(ref)} key={k} className={`ces-item`}>
                            <Image src={i.url} objectFit={'cover'} />
                        </div>
                    ))}
                </div>
            </div>
        </CardsEffectSliderStyle>
    )
})

export default withUIContext(CardsEffectSlider, ['winWidth'])